import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import {
    createPalette,
    DateShortRelative,
    PaletteButton,
    useDateShortRelative,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    Heading,
    LabeledContainer,
    LabeledContainerActionableTitle,
    LabeledContainerBody,
    LabeledContainerHeader,
    List,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
    ListItemSkeleton,
    type ListRenderItemInfo,
    ListSkeleton,
    Tooltip,
} from '@gitbook/spine-react';
import type { MaybePromise } from '@gitbook/util-ts';

import { apiClient, apiQuery } from '../../state';
import { UserAvatar } from '../Users';
import { requestReviewersForChangeRequest } from './requestReviewersForChangeRequest';
import { useAddReviewerPalette } from './useAddReviewerPalette';
import {
    type ChangeRequestPendingRequestedReviewer,
    useChangeRequestReviewers,
} from './useChangeRequestReviewers';

export type ChangeRequestReviewersListProps = {
    changeRequest: api.ChangeRequest;
    space: api.Space;

    /**
     * Placeholder text to display when no reviewers are selected.
     */
    placeholder?: string;

    /**
     * Whether the list is disabled and non editable.
     */
    disabled?: boolean;

    /**
     * List of pending requested reviewers, not yet synced with the backend.
     */
    pendingRequestedReviewers?: ChangeRequestPendingRequestedReviewer[];

    /**
     * Callback when a change is made to the list of pending requested reviewers.
     */
    onPendingRequestedReviewersChange?: (
        pendingRequestedReviewers: ChangeRequestPendingRequestedReviewer[]
    ) => MaybePromise<void>;
};

/**
 * List the reviewers for a change request.
 */
export function ChangeRequestReviewersList(props: ChangeRequestReviewersListProps) {
    const {
        changeRequest,
        space,
        placeholder = 'No reviewers yet.',
        pendingRequestedReviewers = [],
        onPendingRequestedReviewersChange = async (update) => {
            await requestReviewersForChangeRequest({
                spaceId: space.id,
                changeRequestId: changeRequest.id,
                users: update.map((reviewer) => reviewer.userId),
            });
        },
    } = props;

    const { reviews, requestedReviewers, suggestions, existingReviewers, isLoading } =
        useChangeRequestReviewers(space, changeRequest, pendingRequestedReviewers);

    const addReviewerPalette = useAddReviewerPalette({
        space,
        changeRequest,
        suggestions,
        pendingRequestedReviewers,
        existingReviewers,
        onPendingRequestedReviewersChange,
    });

    return (
        <LabeledContainer>
            <LabeledContainerHeader>
                <LabeledContainerActionableTitle>
                    <Heading>Reviewers</Heading>
                    <PaletteButton
                        kind="blank"
                        label="Add and notify reviewers"
                        iconOnly
                        leadingIcon={Icon.Plus}
                        palette={addReviewerPalette}
                    />
                </LabeledContainerActionableTitle>
                {placeholder &&
                requestedReviewers.length === 0 &&
                pendingRequestedReviewers.length === 0 &&
                reviews.length === 0 &&
                !isLoading ? (
                    <Description>{placeholder}</Description>
                ) : null}
            </LabeledContainerHeader>
            <LabeledContainerBody>
                {isLoading ? (
                    <ListSkeleton compact items={1} />
                ) : (
                    <>
                        {requestedReviewers.length > 0 ||
                        pendingRequestedReviewers.length > 0 ||
                        reviews.length > 0 ? (
                            <div className="flex flex-col">
                                {reviews.length > 0 ? (
                                    <List
                                        items={reviews}
                                        renderItem={({
                                            item,
                                        }: ListRenderItemInfo<api.ChangeRequestReview>) => (
                                            <ChangeRequestReviewItem
                                                space={space}
                                                changeRequest={changeRequest}
                                                review={item}
                                            />
                                        )}
                                    />
                                ) : null}
                                {requestedReviewers.length > 0 ? (
                                    <List
                                        items={requestedReviewers}
                                        renderItem={({
                                            item,
                                        }: ListRenderItemInfo<api.ChangeRequestRequestedReviewer>) => (
                                            <ChangeRequestRequestedReviewerItem
                                                space={space}
                                                changeRequest={changeRequest}
                                                reviewer={item}
                                            />
                                        )}
                                    />
                                ) : null}
                                {pendingRequestedReviewers.length > 0 ? (
                                    <List
                                        items={pendingRequestedReviewers}
                                        renderItem={({
                                            item,
                                        }: ListRenderItemInfo<ChangeRequestPendingRequestedReviewer>) => (
                                            <React.Suspense fallback={<ListItemSkeleton compact />}>
                                                <ChangeRequestPendingRequestedReviewerItem
                                                    suggestion={item}
                                                    isRequested={true}
                                                    onRemove={() => {
                                                        return onPendingRequestedReviewersChange(
                                                            pendingRequestedReviewers.filter(
                                                                (reviewer) =>
                                                                    reviewer.userId !== item.userId
                                                            )
                                                        );
                                                    }}
                                                />
                                            </React.Suspense>
                                        )}
                                    />
                                ) : null}
                            </div>
                        ) : null}

                        {suggestions.length > 0 && !isLoading ? (
                            <List
                                items={suggestions}
                                renderItem={({
                                    item,
                                }: ListRenderItemInfo<ChangeRequestPendingRequestedReviewer>) => (
                                    <React.Suspense fallback={<ListItemSkeleton compact />}>
                                        <ChangeRequestPendingRequestedReviewerItem
                                            suggestion={item}
                                            isRequested={false}
                                            onRequest={() => {
                                                return onPendingRequestedReviewersChange([
                                                    ...pendingRequestedReviewers,
                                                    item,
                                                ]);
                                            }}
                                            onRemove={() => {
                                                return onPendingRequestedReviewersChange(
                                                    pendingRequestedReviewers.filter(
                                                        (reviewer) =>
                                                            reviewer.userId !== item.userId
                                                    )
                                                );
                                            }}
                                        />
                                    </React.Suspense>
                                )}
                            />
                        ) : null}
                    </>
                )}
            </LabeledContainerBody>
        </LabeledContainer>
    );
}

/**
 * Item for a review.
 */
function ChangeRequestReviewItem(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    review: api.ChangeRequestReview;
}) {
    const { space, changeRequest, review } = props;

    const { value: humanPeriod } = useDateShortRelative(review.updatedAt, { onlyDate: false });

    return (
        <ListItem compact>
            <ListItemIndicator>
                <UserAvatar user={review.reviewer} size="md" />
            </ListItemIndicator>
            <ListItemBody>
                <ListItemDescription>{review.reviewer.displayName}</ListItemDescription>
                <ListItemDetail>
                    {review.status === api.ChangeRequestReviewStatus.Approved
                        ? 'Approved'
                        : 'Changes requested'}{' '}
                    <DateShortRelative value={review.updatedAt} onlyDate={false} />
                </ListItemDetail>
            </ListItemBody>
            <ListItemControls>
                <Button
                    kind="blank"
                    iconOnly
                    leadingIcon={Icon.Refresh}
                    label="Re-request review"
                    onPress={() => {
                        return requestReviewersForChangeRequest({
                            spaceId: space.id,
                            changeRequestId: changeRequest.id,
                            users: [review.reviewer.id],
                        });
                    }}
                />
            </ListItemControls>
            {review.comment ? (
                <ListItemControls when="always">
                    <Button
                        kind="blank"
                        iconOnly
                        leadingIcon={Icon.Comment}
                        label={`${review.comment.postedBy.displayName} posted a comment with this review`}
                        href={{
                            current: {
                                sideSheet: {
                                    type: 'comments',
                                    comment: review.comment.id,
                                },
                            },
                        }}
                    />
                </ListItemControls>
            ) : null}
            <ListItemControls when="always">
                <div className="pr-2">
                    {review.status === api.ChangeRequestReviewStatus.Approved ? (
                        <Tooltip
                            label={`${review.reviewer.displayName} approved this change request ${humanPeriod}`}
                        >
                            <div className="text-success-standout">
                                <Icon.Check />
                            </div>
                        </Tooltip>
                    ) : (
                        <Tooltip label="Changes requested">
                            <div className="text-danger-standout">
                                <Icon.X />
                            </div>
                        </Tooltip>
                    )}
                </div>
            </ListItemControls>
        </ListItem>
    );
}

/**
 * Item for a requested reviewer.
 */
function ChangeRequestRequestedReviewerItem(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    reviewer: api.ChangeRequestRequestedReviewer;
}) {
    const { space, changeRequest, reviewer } = props;

    if (reviewer.kind !== 'user') {
        return null;
    }

    return (
        <ListItem compact>
            <ListItemIndicator>
                <UserAvatar user={reviewer.user} tooltip={false} size="md" />
            </ListItemIndicator>
            <ListItemBody>
                <ListItemDescription>{reviewer.user.displayName}</ListItemDescription>
                <ListItemDetail>
                    Requested <DateShortRelative value={reviewer.createdAt} onlyDate={false} />
                </ListItemDetail>
            </ListItemBody>
            <ListItemControls>
                <PaletteButton
                    kind="blank"
                    size="small"
                    label="Edit reviewer"
                    iconOnly
                    leadingIcon={Icon.MoreHorizontal}
                    palette={() =>
                        createPalette({
                            groups: [
                                {
                                    key: 'actions',
                                    commands: [
                                        {
                                            key: 're-request',
                                            title: 'Re-request review',
                                            icon: Icon.Refresh,
                                            trigger: async () => {
                                                await requestReviewersForChangeRequest({
                                                    spaceId: space.id,
                                                    changeRequestId: changeRequest.id,
                                                    users: [reviewer.user.id],
                                                });
                                            },
                                        },
                                        {
                                            key: 'remove',
                                            title: 'Remove',
                                            icon: Icon.Trashcan,
                                            trigger: async () => {
                                                await apiClient.changeRequestReviewers.removeRequestedReviewerFromChangeRequest(
                                                    space.id,
                                                    changeRequest.id,
                                                    reviewer.user.id
                                                );
                                            },
                                        },
                                    ],
                                },
                            ],
                        })
                    }
                />
            </ListItemControls>
            <ListItemControls when="always">
                <Tooltip label={`Awaiting requested review from ${reviewer.user.displayName}`}>
                    <div className="text-warning-standout">
                        <Icon.Dot />
                    </div>
                </Tooltip>
            </ListItemControls>
        </ListItem>
    );
}

/**
 * Item that is not yet synced in the backend.
 */
function ChangeRequestPendingRequestedReviewerItem(props: {
    suggestion: ChangeRequestPendingRequestedReviewer;
    isRequested?: boolean;
    onRequest?: () => MaybePromise<void>;
    onRemove?: () => MaybePromise<void>;
}) {
    const { suggestion, isRequested, onRequest, onRemove } = props;
    const { data: user } = useSuspenseQuery(apiQuery('getUserById', { userId: suggestion.userId }));

    return (
        <ListItem compact>
            <ListItemIndicator>
                <UserAvatar user={user} tooltip={false} size="md" />
            </ListItemIndicator>
            <ListItemBody>
                <ListItemDescription>{user.displayName}</ListItemDescription>
                <ListItemDetail>Suggested</ListItemDetail>
            </ListItemBody>
            <ListItemControls when="always">
                <Button
                    kind="blank"
                    leadingIcon={isRequested ? Icon.Check : Icon.Bell}
                    label={isRequested ? 'Requested' : 'Request'}
                    onPress={() => {
                        if (isRequested) {
                            return onRemove?.();
                        } else {
                            return onRequest?.();
                        }
                    }}
                />
            </ListItemControls>
        </ListItem>
    );
}
